import { useEffect, useMemo } from 'react'
import { Card, Flex, Heading } from '@weareberyl/design-system'
import { message, Modal } from 'antd'
import DashboardFlowButton, { DashboardFlow } from './Modal/DashboardFlowModal'
import { JobAutomationSideEffect } from '~/__generated__/globalTypes'
import AutomationItem from './Automation/AutomationItem'
import { isNotNullOrUndefined } from '~/utils/types'
import HeadTitle from '~/components/HeadTitle'
import {
  JobAutomationVehicleInput,
  TriggerDashboardAutomationMutationVariables,
  useDashboardAutomationsQuery,
  useTriggerDashboardAutomationMutation,
} from '~/gql/generated/graphql'
import { ExclamationCircleFilled } from '@ant-design/icons'
import { useAutomationContext } from '~/context/automationContext'

export const DASHBOARD_JUSTIFICATION_PREFIX = 'ui'
const POLL_TIMEOUT = 5 * 60 * 1000 // 5 minutes

const DashboardFlows = () => {
  const variables = {
    filters: {
      prefix: DASHBOARD_JUSTIFICATION_PREFIX,
    },
  }
  const { expectedJobsMap, setExpectedJobsMap, shouldPoll, setShouldPoll } =
    useAutomationContext()
  const [triggerJobAutomationFlow] = useTriggerDashboardAutomationMutation({})
  const removeExpectedJob = (justification_id: string) => {
    setExpectedJobsMap(current => {
      current.delete(justification_id)
      return current
    })
  }
  const setExpectedJobs = (
    justification_id: string,
    expectedJobs: JobAutomationVehicleInput[],
  ) => {
    setExpectedJobsMap(current => {
      current.set(justification_id, expectedJobs)
      return current
    })
    setShouldPoll(true)
    setTimeout(() => setShouldPoll(false), POLL_TIMEOUT)
  }

  const runFlow = (
    variables: TriggerDashboardAutomationMutationVariables,
    text: string,
  ) => {
    const onOk = async variables => {
      try {
        await triggerJobAutomationFlow({ variables })
        setExpectedJobs(variables.justification_id, variables.results)
      } catch (e) {
        message.error(e)
      }
    }

    Modal.confirm({
      title: 'Are you sure?',
      icon: <ExclamationCircleFilled />,
      content: text,
      onOk: () => onOk(variables),
      okText: 'Confirm',
      style: { top: 200 },
      onCancel: undefined,
    })
  }

  const pollInterval = shouldPoll ? 10000 : 0

  const { loading, error, data } = useDashboardAutomationsQuery({
    variables,
    pollInterval,
  })

  const automations = useMemo(() => {
    if (!data?.automations?.length) return []
    return data.automations
      .filter(isNotNullOrUndefined)
      .map(a => ({ ...a, scheme_id: String(a?.jobs[0]?.scheme_id) }))
  }, [data]) as DashboardFlow[]

  useEffect(() => {
    if (!shouldPoll) return

    const expectedEmptyKeys = Array.from(expectedJobsMap.keys()).filter(
      key => expectedJobsMap.get(key)?.length === 0,
    )
    const actualJobKeys = automations.map(
      ({ justification_id }) => justification_id,
    )

    for (const key of expectedEmptyKeys) {
      if (!actualJobKeys?.includes(key)) {
        removeExpectedJob(key)
        setShouldPoll(false)
      }
    }
  }, [automations, expectedJobsMap, shouldPoll])

  if (loading) {
    return null
  }

  if (error) {
    message.error(error.message)
    return null
  }

  const Header = () => (
    <Flex justifyContent="space-between" alignItems="flex-end" mb={5}>
      <Heading variant="callout">Dashboard Flows</Heading>
      <DashboardFlowButton
        setExpectedJobs={setExpectedJobs}
        runFlow={runFlow}
        disabled={shouldPoll}
        loading={shouldPoll}
      />
    </Flex>
  )
  return (
    <>
      <HeadTitle pageTitle="Dashboard Automation Flows" />
      <Card p={5}>
        <Header />
        {automations.map(({ side_effects, jobs, ...props }) => (
          <AutomationItem
            key={props.justification_id}
            expectedJobs={expectedJobsMap.get(props.justification_id)}
            setExpectedJobs={setExpectedJobs}
            side_effects={side_effects as JobAutomationSideEffect[]}
            runFlow={runFlow}
            shouldPoll={shouldPoll}
            setShouldPoll={setShouldPoll}
            jobs={jobs.filter(isNotNullOrUndefined)}
            {...props}
          />
        ))}
      </Card>
    </>
  )
}

export default DashboardFlows
